import React, { useRef, useState } from "react";
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from "@mui/material";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import CloseIcon from "@mui/icons-material/Close";
import { StyledTextField } from "./styled/styledInputs";
import { useExtractAiScoresMutation } from "../pages/quick-entry/recordApiSlice";
import toast from "../../helpers/toast";

/**
 * AIScoreExtractModal — extracts scores from an uploaded image, PDF, or audio file.
 *
 * Props:
 *   open           boolean
 *   onClose        () => void
 *   subjectId      number
 *   classroomId    number
 *   termId         number | null
 *   assessmentId   number | null       — single assessment (CA Manager mode)
 *   assessments    [{id, name, obtainable_score}]  — multiple (Quick Entry mode)
 *   students       [{id, first_name, last_name, reg_number}]
 *   onApply        (rows) => void      — called with [{student_id, assessment_id, score}]
 */

const CONFIDENCE_COLORS = {
  high: "success",
  low: "warning",
  unmatched: "error",
};

const CONFIDENCE_LABELS = {
  high: "High",
  low: "Low match",
  unmatched: "Unmatched",
};

const STEP = { UPLOAD: 0, LOADING: 1, REVIEW: 2 };

export default function AIScoreExtractModal({
  open,
  onClose,
  subjectId,
  classroomId,
  termId = null,
  assessmentId = null,
  assessments = [],
  students = [],
  onApply,
}) {
  const [step, setStep] = useState(STEP.UPLOAD);
  const [file, setFile] = useState(null);
  const [rows, setRows] = useState([]); // [{student_id, student_name, extracted_name, reg_number, assessment_id, assessment_name, score, confidence}]
  const fileInputRef = useRef(null);

  const [extractAiScores] = useExtractAiScoresMutation();

  const handleClose = () => {
    setStep(STEP.UPLOAD);
    setFile(null);
    setRows([]);
    onClose();
  };

  const handleExtract = async () => {
    if (!file) {
      toast.error("Please upload a file first");
      return;
    }
    setStep(STEP.LOADING);

    const formData = new FormData();
    formData.append("file", file);
    if (subjectId) formData.append("subject_id", subjectId);
    if (classroomId) formData.append("classroom_id", classroomId);
    if (termId) formData.append("term_id", termId);

    if (assessmentId) {
      formData.append("assessment_id", assessmentId);
    } else if (assessments.length > 0) {
      formData.append("assessments", JSON.stringify(assessments.map((a) => a.id)));
    }

    try {
      const result = await extractAiScores(formData).unwrap();
      const extractions = result.extractions || [];
      setRows(
        extractions.map((e) => ({
          student_id: e.student_id,
          student_name: e.student_name || e.extracted_name,
          extracted_name: e.extracted_name,
          reg_number: e.reg_number,
          assessment_id: e.assessment_id,
          assessment_name: e.assessment_name,
          score: e.score != null ? String(e.score) : "",
          confidence: e.confidence,
        }))
      );
      setStep(STEP.REVIEW);
    } catch (err) {
      toast.error(err?.data?.error || "Failed to extract scores");
      setStep(STEP.UPLOAD);
    }
  };

  const handleScoreChange = (idx, value) => {
    setRows((prev) => prev.map((r, i) => (i === idx ? { ...r, score: value } : r)));
  };

  const handleRemoveRow = (idx) => {
    setRows((prev) => prev.filter((_, i) => i !== idx));
  };

  const handleApply = () => {
    const toApply = rows
      .filter((r) => r.student_id != null && r.assessment_id != null && r.score !== "")
      .map((r) => ({
        student_id: r.student_id,
        assessment_id: r.assessment_id,
        score: parseFloat(r.score) || 0,
      }));

    if (toApply.length === 0) {
      toast.error("No valid scores to apply");
      return;
    }
    onApply(toApply);
    handleClose();
  };

  const matchedCount = rows.filter((r) => r.student_id != null).length;

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle
        sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}
      >
        <Stack direction="row" spacing={1} alignItems="center">
          <AutoAwesomeIcon color="primary" />
          <Typography variant="h6">AI Score Extraction</Typography>
        </Stack>
        <IconButton onClick={handleClose} size="small">
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers>
        {/* ── Step 0: Upload ── */}
        {step === STEP.UPLOAD && (
          <Stack spacing={3} sx={{ pt: 1 }}>
            <Typography variant="body2" color="text.secondary">
              Upload a photo of a mark sheet, a PDF/Word document, or an audio
              recording of scores being read aloud. The AI will extract student
              names and scores and match them to the enrolled class list.
            </Typography>

            <Box>
              <input
                ref={fileInputRef}
                type="file"
                accept="audio/*,image/*,.pdf,.doc,.docx"
                style={{ display: "none" }}
                onChange={(e) => setFile(e.target.files?.[0] || null)}
              />
              <Button
                variant="outlined"
                startIcon={<UploadFileIcon />}
                onClick={() => fileInputRef.current?.click()}
              >
                {file ? file.name : "Choose file"}
              </Button>
              {file && (
                <IconButton
                  size="small"
                  sx={{ ml: 1 }}
                  onClick={() => {
                    setFile(null);
                    if (fileInputRef.current) fileInputRef.current.value = "";
                  }}
                >
                  <CloseIcon fontSize="small" />
                </IconButton>
              )}
            </Box>

            {assessmentId && (
              <Typography variant="caption" color="text.secondary">
                Extracting scores for:{" "}
                <strong>
                  {assessments.find((a) => a.id === assessmentId)?.name ||
                    `Assessment #${assessmentId}`}
                </strong>
              </Typography>
            )}
            {!assessmentId && assessments.length > 0 && (
              <Typography variant="caption" color="text.secondary">
                Extracting scores for:{" "}
                <strong>{assessments.map((a) => a.name).join(", ")}</strong>
              </Typography>
            )}
          </Stack>
        )}

        {/* ── Step 1: Loading ── */}
        {step === STEP.LOADING && (
          <Box sx={{ py: 6, textAlign: "center" }}>
            <CircularProgress />
            <Typography variant="body2" sx={{ mt: 2 }} color="text.secondary">
              Extracting scores…
            </Typography>
          </Box>
        )}

        {/* ── Step 2: Review ── */}
        {step === STEP.REVIEW && (
          <Box>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              {matchedCount} of {rows.length} entries matched to enrolled students. Review
              scores, correct errors, and remove any rows you don't want to apply.
            </Typography>
            <TableContainer sx={{ overflowX: "auto" }}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 600 }}>Matched Student</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Extracted As</TableCell>
                    {!assessmentId && rows.some((r) => r.assessment_name) && (
                      <TableCell sx={{ fontWeight: 600 }}>Assessment</TableCell>
                    )}
                    <TableCell sx={{ fontWeight: 600, width: 100 }}>Score</TableCell>
                    <TableCell sx={{ fontWeight: 600, width: 100 }}>Confidence</TableCell>
                    <TableCell sx={{ width: 48 }} />
                  </TableRow>
                </TableHead>
                <TableBody>
                  {rows.map((row, idx) => (
                    <TableRow
                      key={idx}
                      sx={{
                        background:
                          row.confidence === "unmatched"
                            ? "rgba(211,47,47,0.04)"
                            : row.confidence === "low"
                            ? "rgba(255,152,0,0.04)"
                            : undefined,
                      }}
                    >
                      <TableCell>
                        {row.student_id ? (
                          <Typography variant="body2">{row.student_name}</Typography>
                        ) : (
                          <Typography variant="body2" color="error.main">
                            Not matched
                          </Typography>
                        )}
                        {row.reg_number && (
                          <Typography variant="caption" color="text.secondary">
                            {row.reg_number}
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        <Typography variant="caption" color="text.secondary">
                          "{row.extracted_name}"
                        </Typography>
                      </TableCell>
                      {!assessmentId && rows.some((r) => r.assessment_name) && (
                        <TableCell>
                          <Typography variant="caption">
                            {row.assessment_name || "—"}
                          </Typography>
                        </TableCell>
                      )}
                      <TableCell>
                        <StyledTextField
                          type="number"
                          value={row.score}
                          onChange={(e) => handleScoreChange(idx, e.target.value)}
                          inputProps={{ step: "any", min: 0 }}
                          size="small"
                          sx={{ width: 80 }}
                        />
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={CONFIDENCE_LABELS[row.confidence]}
                          color={CONFIDENCE_COLORS[row.confidence]}
                          size="small"
                          variant="outlined"
                        />
                      </TableCell>
                      <TableCell>
                        <Tooltip title="Remove row">
                          <IconButton size="small" onClick={() => handleRemoveRow(idx)}>
                            <CloseIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
            {rows.some((r) => r.confidence === "unmatched") && (
              <Typography variant="caption" color="error" sx={{ mt: 1, display: "block" }}>
                Unmatched rows will not be applied. Remove them or correct the score sheet
                and re-extract.
              </Typography>
            )}
          </Box>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={handleClose}>Cancel</Button>
        {step === STEP.UPLOAD && (
          <Button
            variant="contained"
            onClick={handleExtract}
            startIcon={<AutoAwesomeIcon />}
            disabled={!file}
          >
            Extract
          </Button>
        )}
        {step === STEP.REVIEW && (
          <>
            <Button onClick={() => setStep(STEP.UPLOAD)}>Back</Button>
            <Button
              variant="contained"
              onClick={handleApply}
              disabled={rows.filter((r) => r.student_id && r.score !== "").length === 0}
            >
              Apply to Score Sheet (
              {rows.filter((r) => r.student_id && r.score !== "").length})
            </Button>
          </>
        )}
      </DialogActions>
    </Dialog>
  );
}
